import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getImageDisplayMetrics } from './colorUtils';

/**
 * Region selection and inpainting (erase) logic hook
 */
export const useRegionErase = ({ canvasRef, imageRef }) => {
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Draw selected regions on canvas
  const drawRegions = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    const m = getImageDisplayMetrics(image);
    const containerRect = image.getBoundingClientRect();

    canvas.width = containerRect.width;
    canvas.height = containerRect.height;
    canvas.dataset.offsetX = m.offsetX;
    canvas.dataset.offsetY = m.offsetY;
    canvas.dataset.displayWidth = m.displayWidth;
    canvas.dataset.displayHeight = m.displayHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    [...selectedRegions, currentRegion].filter(Boolean).forEach((region, index) => {
      const x = m.offsetX + region.x * m.displayWidth;
      const y = m.offsetY + region.y * m.displayHeight;
      const width = region.width * m.displayWidth;
      const height = region.height * m.displayHeight;

      ctx.fillStyle = 'rgba(239, 68, 68, 0.3)';
      ctx.fillRect(x, y, width, height);
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x, y, width, height);

      if (region !== currentRegion) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.9)';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText(`${index + 1}`, x + 5, y + 18);
      }
    });
  }, [selectedRegions, currentRegion, canvasRef, imageRef]);

  useEffect(() => { drawRegions(); }, [drawRegions]);

  const getImageCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const ox = parseFloat(canvas.dataset.offsetX) || 0;
    const oy = parseFloat(canvas.dataset.offsetY) || 0;
    const dw = parseFloat(canvas.dataset.displayWidth) || rect.width;
    const dh = parseFloat(canvas.dataset.displayHeight) || rect.height;
    if (mouseX < ox || mouseX > ox + dw || mouseY < oy || mouseY > oy + dh) return null;
    return { x: (mouseX - ox) / dw, y: (mouseY - oy) / dh };
  };

  const handleMouseDown = (e, selectedMode, previewUrl) => {
    if (selectedMode !== 'erase' || !previewUrl) return;
    const coords = getImageCoordinates(e);
    if (!coords) return;
    setIsDrawing(true);
    setCurrentRegion({ x: coords.x, y: coords.y, width: 0, height: 0 });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !currentRegion) return;
    const coords = getImageCoordinates(e);
    if (!coords) return;
    setCurrentRegion(prev => ({
      ...prev,
      width: Math.max(0.01, coords.x - prev.x),
      height: Math.max(0.01, coords.y - prev.y),
    }));
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentRegion) return;
    if (currentRegion.width > 0.02 && currentRegion.height > 0.02) {
      setSelectedRegions(prev => [...prev, currentRegion]);
    }
    setIsDrawing(false);
    setCurrentRegion(null);
  };

  const removeRegion = (index) => setSelectedRegions(prev => prev.filter((_, i) => i !== index));

  const clearAllRegions = () => {
    setSelectedRegions([]);
    setResultImageUrl(null);
  };

  // Inpainting execution
  const handleInpaint = async (s3ImageUrl) => {
    if (!s3ImageUrl) { toast.error('Please upload an image first'); return; }
    if (selectedRegions.length === 0) { toast.error('Please select a region to erase'); return; }

    setIsProcessing(true);
    try {
      const WS_URL = import.meta.env.VITE_LABS_WS_URL || 'wss://uknjkyctd3.execute-api.us-east-1.amazonaws.com/prod';
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        ws.send(JSON.stringify({
          action: 'sendMessage',
          inpaintAction: 'inpaint',
          imageUrl: s3ImageUrl,
          maskRegions: selectedRegions,
          prompt: 'clean background, seamless blend, remove watermark and logo',
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'complete' && message.visualData?.type === 'inpaint_result') {
            setResultImageUrl(message.visualData.data.resultUrl);
            setIsProcessing(false);
            toast.success('Region erase complete!');
            ws.close();
          } else if (message.type === 'error') {
            throw new Error(message.error);
          }
        } catch (e) {
          setIsProcessing(false);
          toast.error('Processing failed: ' + e.message);
          ws.close();
        }
      };

      ws.onerror = () => { setIsProcessing(false); toast.error('Server connection failed'); };
    } catch { setIsProcessing(false); toast.error('Processing failed'); }
  };

  // Download result
  const handleDownload = async () => {
    if (!resultImageUrl) return;
    try {
      const response = await fetch(resultImageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'chart_cleaned.png';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Image downloaded');
    } catch { toast.error('Download failed'); }
  };

  // Copy result
  const handleCopyImage = async () => {
    if (!resultImageUrl) return;
    try {
      const response = await fetch(resultImageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Image copied to clipboard');
    } catch { toast.error('Failed to copy image. Your browser may not support this.'); }
  };

  const resetErase = () => {
    setSelectedRegions([]);
    setResultImageUrl(null);
  };

  return {
    selectedRegions, setSelectedRegions,
    resultImageUrl, setResultImageUrl,
    isProcessing,
    drawRegions,
    handleMouseDown, handleMouseMove, handleMouseUp,
    removeRegion, clearAllRegions,
    handleInpaint, handleDownload, handleCopyImage,
    resetErase,
  };
};
